import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  CategoryScale,
  Chart,
  ChartOptions,
  Legend,
  LineElement,
  LinearScale,
  LogarithmicScale,
  PointElement,
  Tooltip,
} from "chart.js";
import Datalabels from "chartjs-plugin-datalabels";
import { Line } from "react-chartjs-2";
import { Item } from "reakit/ts/Composite/__utils/types";
import styled from "styled-components";
import { Serie } from "@charty/models";
import { useTheme } from "@glossy/theme";
import { AbstractDate, Range } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import {
  updateLineChartOnHover,
  useChartData,
  useDatasets,
  useOnClickPoint,
} from "./data";
import { getOptions } from "./options";

Chart.register(
  CategoryScale,
  LineElement,
  LinearScale,
  LogarithmicScale,
  PointElement,
  Tooltip,
  Legend,
  Datalabels
);

type TooltipSerie = {
  count: number;
  label: string;
  color: string;
  date: AbstractDate;
};

export interface ChartProps {
  hovered: string;
  onClickPoint: (
    uid: Item["id"] | Item["id"][],
    range: { from: AbstractDate; to: AbstractDate }
  ) => void;
  config?: Partial<ChartOptions>;
  range: Range;
  locale: Locale;
  series: Serie[];
  chartTooltip?: (series: TooltipSerie[]) => ReactNode;
  scale?: "logarithmic" | "linear";
  timezone: Timezone;
  disableAnimation?: boolean;
  isFullscreen?: boolean;
}

const Root = styled.div`
  position: relative;
  height: calc(100% - 36px);
`;

const LinechartChart = ({
  hovered,
  config,
  chartTooltip,
  range,
  locale,
  series,
  onClickPoint,
  scale = "linear",
  timezone,
  disableAnimation = false,
  isFullscreen,
}: ChartProps) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const datasets = useDatasets(series, range, hovered);
  const [hasAnimated, setHasAnimated] = useState(disableAnimation);
  const data = useChartData(datasets, range);
  const _onClickPoint = useOnClickPoint(
    datasets,
    series,
    range,
    timezone,
    onClickPoint
  );
  const options = useMemo(
    () =>
      getOptions(
        theme,
        range.interval,
        locale,
        timezone,
        onClickPoint ? _onClickPoint : null,
        series,
        chartTooltip,
        config,
        scale,
        setHasAnimated,
        hasAnimated,
        isFullscreen
      ),
    [
      theme,
      range.interval,
      locale,
      timezone,
      onClickPoint,
      _onClickPoint,
      series,
      chartTooltip,
      config,
      scale,
      hasAnimated,
      isFullscreen,
    ]
  );
  useEffect(() => {
    if (chartRef.current) {
      updateLineChartOnHover(chartRef.current, hovered, theme);
      chartRef.current.update();
    }
  }, [hovered, chartRef, theme]);

  return (
    <Root>
      <Line data={data} options={options} ref={chartRef} />
    </Root>
  );
};

export default LinechartChart;
